<template>
    <div id="question-setting-modal" :class="[{ 'modal fade p-2': true, show: show, close: !show }]">
        <div @click="validateBeforeClose()" class="bg-backdrop absolute w-full h-full"></div>
        <div class="modal-content w-full h-full flex flex-col relative">
            <div class="modal-header flex justify-between items-center p-4">
                <h3 class="m-0 shadow-text-none">
                    {{ modalHeader }}
                    <template v-if="questions.length"> {{ currentIndex + 1 }}/{{ questions.length }}</template>
                    <CrownIcon v-if="showCrownIcon" id="crown-icon-header" class="font-size-18" />
                </h3>
                <div class="close-box relative">
                    <span v-if="isLoading" class="close-loader"></span>
                    <i
                        v-else
                        @click="validateBeforeClose()"
                        class="fas fa-times text-xl shadow-text-none cursor-pointer"
                    ></i>
                </div>
            </div>
            <div
                :class="[
                    {
                        'modal-body w-full h-full font-size-16 relative overflow-x-hidden': true,
                        'overflow-y-auto': !isLoading
                    }
                ]"
                id="question-setting-body"
            >
                <div
                    v-for="(question, index) in showQuestions"
                    :key="index"
                    :class="{
                        'question-setting-content w-full h-full': true,
                        left: index === 0,
                        center: index === 1,
                        right: index === 2
                    }"
                >
                    <QuizCreatorLoader v-if="!Object.values(question).length && isLoading" />
                    <div v-if="Object.values(question).length && questions[question.questionIndex]" class="pb-16">
                        <!-- Question Section -->
                        <QuestionSettingSection
                            @open-spotify-popup="showSpotifyPopup = true"
                            @validate-result="setValidateResult($event)"
                            :question="questions[question.questionIndex]"
                            :question-index="question.questionIndex"
                            :audio-spotify="audioSpotify"
                            ref="questionSettingSection-0"
                        />

                        <!-- Answer Section -->
                        <AnswerSettingSection
                            v-if="questions[question.questionIndex].question_type != 'puzzle'"
                            @validate-result="setValidateResult($event)"
                            :question-index="question.questionIndex"
                            :question-type="questions[question.questionIndex].question_type"
                            :answer-required="questions[question.questionIndex].answers_required"
                            :answers="questions[question.questionIndex].answers"
                            ref="answerSettingSection-0"
                        />

                        <!-- option for hotspot -->
                        <div v-if="questions[question.questionIndex].question_type == 'hotspot'" class="px-4 pt-4">
                            <XCheckbox
                                @input="toggleQuestionIsAnywhere($event)"
                                :checked="questions[question.questionIndex].is_anywhere"
                                :disabled="!canEditQuiz"
                                :square="true"
                                type="checkbox"
                                size="lg"
                                theme="dark"
                            >
                                <span id="anywhere-checkbox" class="shadow-text-none">
                                    {{ $t('question_anywhere_give_wrong') }}
                                </span>
                            </XCheckbox>
                        </div>

                        <XDivider class="mt-10" color="#ccc" />

                        <!-- Other Settings -->
                        <div v-if="quizType != 'poll'" class="p-4">
                            <XCheckbox
                                @input="toggleQuestionMandatory($event)"
                                :checked="questions[question.questionIndex].is_mandatory"
                                :square="true"
                                :disabled="!canEditQuiz"
                                type="checkbox"
                                size="lg"
                                theme="dark"
                            >
                                <span id="mandatory-checkbox" class="shadow-text-none">{{
                                    $t('question_mandatory')
                                }}</span>
                            </XCheckbox>
                        </div>
                        <div class="p-4">
                            <XToggleSwitch
                                v-if="show"
                                @input="toggleQuestionStatus($event)"
                                :checked="questions[question.questionIndex].status"
                                :border-checked-active="true"
                                :content="$t('question_active')"
                                :disabled="!canEditQuiz"
                                class="shadow-text-none"
                                height="24"
                                color="#5a5351"
                                border-weight="3"
                                border-color="#5a5351"
                                checked-color="#28a745"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer flex justify-between items-center p-4">
                <span
                    @click="prevQuestion()"
                    :class="[
                        {
                            'arrow shadow-text-none cursor-pointer': true,
                            'disabled pointer-events-none': currentIndex === 0 || isLoading
                        }
                    ]"
                >
                    <i class="fas fa-chevron-left"></i>
                </span>
                <span
                    @click="addQuestion()"
                    :class="[
                        {
                            'add-question-btn shadow-text-none cursor-pointer': true,
                            'disabled pointer-events-none': isLoading || !canEditQuiz
                        }
                    ]"
                >
                    {{ $t('question_modal_button_add_new_question') }}
                </span>
                <span
                    @click="nextQuestion()"
                    :class="[
                        {
                            'arrow shadow-text-none cursor-pointer': true,
                            'disabled pointer-events-none': currentIndex === questions.length - 1 || isLoading
                        }
                    ]"
                >
                    <i class="fas fa-chevron-right"></i>
                </span>
            </div>
        </div>

        <SpotifySelectPopup
            v-if="showSpotifyPopup"
            @selected="onSelectTrack"
            @close="closeSpotifyPopup"
            :backdrop-animate="false"
            id="spotify-modal"
        />
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import swal from 'sweetalert'
import QuestionSettingSection from '@/components/quiz-creator/question-section/questions/QuestionSettingSection'
import AnswerSettingSection from '@/components/quiz-creator/question-section/answers/AnswerSettingSection'
import XDivider from '@/components/v2/XDivider.vue'
import XCheckbox from '@/components/v2/XCheckbox'
import XToggleSwitch from '@/components/v2/XToggleSwitch'
import SpotifySelectPopup from '@/components/SpotifySelectPopup'
import QuizCreatorLoader from '@/components/quiz-creator/QuizCreatorLoader'
import IntroHint from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'
import CrownIcon from '@/components/CrownIcon'
import quizCreator from '@/mixins/quiz-creator'

export default {
    components: {
        QuestionSettingSection,
        AnswerSettingSection,
        XDivider,
        XCheckbox,
        XToggleSwitch,
        SpotifySelectPopup,
        QuizCreatorLoader,
        CrownIcon
    },

    mixins: [introJsData, quizCreator],

    props: {
        questions: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            show: true,
            isLoading: false,
            modalHeader: null,
            cacheQuestions: [],
            currentIndex: 0,
            showSpotifyPopup: false,
            introHint: new IntroHint(),

            questionError: [],
            answerError: [],

            showQuestions: []
        }
    },

    mounted() {
        // call from mixin introJsData
        this.openSlideFromHint()
    },

    computed: {
        ...mapState({
            quizType: state => state.question.type,
            uploadMedia: state => state.uploadMedia,
            isShowPaymentPopup: state => state.isShowPaymentPopup,
            showSchoolList: state => state.question.show_school_list,
            showSchoolCountryList: state => state.question.show_school_country_list,
            minimumQuestionToShare: state => state.minimumQuestionToShare,
            showCrownIcon() {
                return (
                    this.questions.length > 0 &&
                    this.questions[this.currentIndex] &&
                    this.questions[this.currentIndex].question_type !== 'basic'
                )
            }
        }),

        ...mapGetters({
            audioSpotify: 'getSpotifyAudio',
            paymentModalTitle: 'paymentModalTitle',
            paymentModalDescription: 'paymentModalDescription'
        }),

        validateErrors() {
            return [...new Set([...this.questionError, ...this.answerError])]
        }
    },

    methods: {
        async showModal(headerType, questionIndex) {
            this.isLoading = true

            this.setHeader(headerType)

            this.currentIndex = questionIndex

            this.cacheQuestions = JSON.parse(JSON.stringify(this.questions))

            this.cacheQuestions = this.cacheQuestions.filter(question => !question.onCreate)

            await this.generateQuestion()

            if (questionIndex >= 1) {
                this.introHint.removeHints()
            } else {
                this.introHint.removeHints()
                setTimeout(() => {
                    this.introHint.register(this.hintsCreateQuestion)
                }, 500)
            }

            this.isLoading = false
        },

        async generateQuestion() {
            this.showQuestions = []

            await this.$nextTick()

            this.setShowQuestions()

            return true
        },

        setShowQuestions() {
            let questions = JSON.parse(JSON.stringify(this.questions))

            questions = questions.map((question, index) => {
                return {
                    ...question,
                    questionIndex: index
                }
            })

            this.showQuestions = [{}, questions[this.currentIndex], {}]
        },

        async validateBeforeClose() {
            if (this.$refs[`questionSettingSection-0`]) {
                this.$refs[`questionSettingSection-0`][0].removeHint()
            }

            if (this.isLoading == true) return

            if (JSON.stringify(this.questions) !== JSON.stringify(this.cacheQuestions)) {
                this.isLoading = true
            }

            const validate = await this.validateQuestion()

            if (!validate) {
                this.isLoading = false
                return
            }

            await this.$nextTick()

            this.$emit('close', this.cacheQuestions)
        },

        closeModal() {
            this.closeAllMedia()
            this.show = false
        },

        setHeader(type) {
            this.modalHeader =
                type == 'create'
                    ? this.$t('question_modal_header_create_question')
                    : this.$t('question_modal_header_edit_question')
        },

        async prevQuestion() {
            await this.validateCurrentSlide()

            if (this.validateErrors.length) return

            if (this.currentIndex == 1) {
                this.introHint.removeHints()
                setTimeout(() => {
                    this.introHint.register(this.hintsCreateQuestion)
                }, 500)
            } else {
                this.introHint.removeHints()
            }

            if (this.currentIndex === 0) return

            this.closeAllMedia()

            this.changeSlide('left')
        },

        async nextQuestion() {
            await this.validateCurrentSlide()

            if (this.validateErrors.length) return

            this.introHint.removeHints()

            if (this.currentIndex === this.questions.length - 1) return

            this.closeAllMedia()

            this.changeSlide('right')
        },

        changeSlide(position) {
            const left = document.querySelector('.question-setting-content.left')
            const center = document.querySelector('.question-setting-content.center')
            const right = document.querySelector('.question-setting-content.right')

            if (position === 'left') {
                left.style.left = '0%'
                center.style.left = '100%'
                right.style.left = '200%'

                this.currentIndex = this.currentIndex - 1
            }

            if (position === 'right') {
                left.style.left = '-200%'
                center.style.left = '-100%'
                right.style.left = '0%'

                this.currentIndex = this.currentIndex + 1
            }

            if (position === 'new') {
                left.style.left = '-200%'
                center.style.left = '-100%'
                right.style.left = '0%'

                this.currentIndex = this.questions.length - 1
            }

            this.isLoading = true

            setTimeout(async () => {
                await this.generateQuestion(this.currentIndex)
                this.isLoading = false
            }, 300)
        },

        closeAllMedia() {
            if (this.questions[this.currentIndex] && this.questions[this.currentIndex].question_type == 'music') {
                this.audioSpotify.pause()
            }

            if (
                this.$refs['questionSettingSection-0'][0] &&
                this.$refs['questionSettingSection-0'][0].$refs.videoPlayer
            ) {
                this.$refs['questionSettingSection-0'][0].$refs.videoPlayer.stopVideo()
            }
        },

        async addQuestion() {
            await this.validateCurrentSlide()

            if (this.validateErrors.length) return

            this.introHint.removeHints()

            this.closeAllMedia()

            this.$store.commit('addNewQuestion')

            this.changeSlide('new')
        },

        toggleQuestionIsAnywhere(event) {
            this.$store.commit('toggleQuestionIsAnywhere', {
                questionIndex: this.currentIndex,
                value: event.target.checked
            })
        },

        toggleQuestionMandatory(event) {
            this.$store.commit('toggleQuestionMandatory', {
                questionIndex: this.currentIndex,
                value: event.target.checked
            })
        },

        toggleQuestionStatus(event) {
            this.$store.commit('toggleQuestionStatus', {
                questionIndex: this.currentIndex,
                value: event.target.checked
            })

            if (
                this.questions.filter(question => question.status).length < this.minimumQuestionToShare &&
                (this.showSchoolList || this.showSchoolCountryList)
            ) {
                this.$emit('open-share-validate-modal')
            }
        },

        onSelectTrack(track) {
            this.$store.commit('addSong', { questionIndex: this.currentIndex, track })
            this.closeSpotifyPopup()
        },

        closeSpotifyPopup() {
            document.querySelector('#spotify-modal').classList.remove('show')
            setTimeout(() => {
                this.showSpotifyPopup = false
            }, 300)
        },

        async validateQuestion() {
            await this.validateCurrentSlide()

            if (this.validateErrors.length) {
                const message = this.$t('setting_confirm_question_unsaved')
                const swalOption = {
                    buttons: {
                        confirm: {
                            text: 'Edit',
                            value: true,
                            className: 'btn font-opensans text-md system-button-color system-button-text-color'
                        },
                        cancel: {
                            text: 'Close',
                            visible: true,
                            value: false,
                            className: 'btn default font-opensans text-md'
                        }
                    },
                    closeOnClickOutside: false
                }

                const confirmation = await swal(message, swalOption)

                if (confirmation) {
                    return false
                } else {
                    return new Promise(resolve => {
                        const validateSort = this.validateErrors.sort((a, b) => b - a)

                        for (const questionIndex of validateSort) {
                            if (this.questions[questionIndex].onCreate) {
                                this.$store.commit('removeQuestion', questionIndex)
                                this.currentIndex = this.questions.length - 1
                                continue
                            }

                            this.$store.commit('setQuestion', {
                                questionIndex,
                                question: this.cacheQuestions[questionIndex]
                            })
                        }

                        resolve(true)
                    })
                }
            } else {
                return true
            }
        },

        async validateCurrentSlide() {
            await this.generateQuestion()

            await this.$refs['questionSettingSection-0'][0].validateQuestionSection()

            if (this.showQuestions[1].question_type != 'puzzle') {
                await this.$refs['answerSettingSection-0'][0].validateAnswerSection()
            }

            if (this.showQuestions[1].question_type == 'hotspot') {
                await this.$refs['questionSettingSection-0'][0].validateHotspotQuestion()
            }

            return true
        },

        setValidateResult(result) {
            if (result.section === 'question') {
                if (result.passed) {
                    if (typeof this.questionError.find(index => index === result.questionIndex) !== 'undefined') {
                        this.questionError.splice(
                            this.questionError.findIndex(index => index === result.questionIndex),
                            1
                        )
                    }
                } else {
                    this.questionError.push(result.questionIndex)
                    this.questionError = [...new Set(this.questionError)]
                }
            }

            if (result.section === 'answer') {
                if (result.passed) {
                    if (typeof this.answerError.find(index => index === result.questionIndex) !== 'undefined') {
                        this.answerError.splice(
                            this.answerError.findIndex(index => index === result.questionIndex),
                            1
                        )
                    }
                } else {
                    this.answerError.push(result.questionIndex)
                    this.answerError = [...new Set(this.answerError)]
                }
            }
        }
    }
}
</script>

<style lang="scss">
#question-setting-modal {
    #question-setting-body {
        position: relative;
        scroll-behavior: smooth;

        .question-setting-content {
            position: absolute;
            top: 0;
            left: 0;
            transition: left 0.3s;

            &:first-child {
                left: -100%;
            }

            &:last-child {
                left: 100%;
            }
        }
    }

    .input-option-btn {
        top: 50%;
        right: 0.5rem;
        transform: translateY(-50%);
        column-gap: 0.5rem;

        .image-icon {
            font-size: 26px;
            color: #463e3c;
            text-shadow: none;
        }
    }

    .modal-footer {
        .add-question-btn,
        .arrow {
            transition: color 0.3s;

            &.disabled {
                color: #ccc;
            }
        }
    }

    .crown-icon {
        top: 1.15rem;
        color: #dc3545;
    }

    .close-box {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
    }

    .close-loader {
        position: absolute;
        top: 0;
        left: 0;
        width: 20px;
        height: 20px;
        border: 5px solid #333;
        border-bottom-color: transparent;
        border-radius: 50%;
        display: inline-block;
        box-sizing: border-box;
        animation: rotation 1s linear infinite;
    }

    @keyframes rotation {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
}
</style>
